import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchBanner, fetchBanners, createBanner, updateBanner, reorderBanners } from "src/actions/mine_craft/bannersActions";

export interface BannerInterface {
  url: string;

  id: number;
  app_id: number;

  user_id?: number | null;
  user?: number | null;

  image_url: string;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null

  newParams: BannerInterface | undefined;
  newError: string | null;

  newGroupParams: BannerInterface | undefined;
  newGroupError: string | null;

  reorderIds: number[] | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  newGroupParams: undefined,
  newGroupError: null,

  reorderIds: null,
};

const adaptor = createEntityAdapter<BannerInterface>();

const slice = createSlice({
  name: 'banners',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      const defaults = {}

      state.newParams = action.payload ? { ...defaults, ...action.payload } : undefined;
      state.newError  = initialState.newError;
    },
    setNewGroupParams: (state, action) => {
      state.newGroupParams = action.payload;
      state.newGroupError  = initialState.newGroupError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setReorderIds: (state, action) => {
      state.reorderIds = action.payload.ids;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchBanners.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.banners;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createBanner.fulfilled, (state, action) => {
        const banner = Object.values(action.payload.banner)[0];
        state.entities[banner.id] = banner;
      })
      .addCase(createBanner.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchBanner.fulfilled, (state, action) => {
        const banner = Object.values(action.payload.banner)[0];
        state.entities[banner.id] = banner;
      })

      // #update
      .addCase(updateBanner.fulfilled, (state, action) => {
        const banner = Object.values(action.payload.banner)[0];
        state.entities[banner.id] = banner;
      })
      .addCase(updateBanner.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reorder
      .addCase(reorderBanners.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewGroupParams,
  setNewError,
  setEditError,
  setReorderIds
} = slice.actions;

export const {
  selectById: selectBannerById,
  selectIds: selectBannerIds,
  selectEntities: selectBannerEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.banners);
